import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { getExtStyles } from './getExtStyles';
import { ClipIconSize } from './types';

export type FileExtensionPreviewSize = 'small' | 'mediumSmall' | 'medium' | 'large';
export type FileExtensionPreviewColor = 'gray' | 'color' | 'default';

export type FileExtensionPreviewProps = {
  /** clip extension used to differentiate file icons and colors */
  ext: string;
  /** text we want to show below the icon */
  text?: string;
  /** color of icon & text with three types */
  color?: FileExtensionPreviewColor;
  /** size of icon & text with three types */
  size?: FileExtensionPreviewSize;

  className?: string;
};

/**
 * This component is used to to differentiate files by icon and color based on the file's extension.
 * Styling is different for icons on the modal, in file table view, and file gallery view.
 * Icons are grey when processing a new upload.
 */
export const FileExtensionPreview = memo(
  ({ ext, color = 'color', size = 'small', text, className }: FileExtensionPreviewProps) => {
    const sizeMap: { [key in string]: { icon: ClipIconSize; font?: 8 | 10 | 12; textMarginTop?: number } } = {
      medium: {
        font: 12,
        icon: ClipIconSize.medium,
        textMarginTop: 10,
      },
      mediumSmall: {
        font: 10,
        icon: ClipIconSize.mediumSmall,
        textMarginTop: 2,
      },
      small: {
        font: 8,
        icon: ClipIconSize.small,
        textMarginTop: 2,
      },
    };

    const { iconName, iconComponent: IconComponent, iconColor, modalIconColor } = getExtStyles(ext);

    const colorMap: { [key in string]: string } = {
      color: iconColor,
      default: modalIconColor,
      gray: 'var(--colors-gray-9)',
    };

    const extIconColor = 'currentColor';

    return (
      <div
        className={cn('flex flex-col items-center', className)}
        data-testid="EXT_PREVIEW_BOX"
        style={{ color: colorMap[color] }}
      >
        <IconComponent data-testid={iconName} size={sizeMap[size].icon} style={{ color: extIconColor }} />
        {!!text && (
          <div
            className="text-center font-bold text-14 uppercase"
            data-testid="ASSET_STATE_TEXT"
            style={{
              // using fontSize because size 8 font (uppercased is a visual 10) is not in our TextVariant system
              fontSize: sizeMap[size].font,
              marginTop: sizeMap[size].textMarginTop,
            }}
          >
            {text}
          </div>
        )}
      </div>
    );
  },
);

FileExtensionPreview.displayName = 'FileExtensionPreview';
